import { useEffect, useMemo, useRef, useState } from "react";
import {
  Circle,
  Headphones,
  MessageCircle,
  Paperclip,
  Plus,
  Send,
  ShieldCheck,
  X
} from "lucide-react";
import {
  addMessage,
  createTicket,
  isManagementHostname,
  resolveTenantId,
  subscribeMessages,
  subscribeTickets,
  updateTicket
} from "../services/supportService";

const statuses = ["new", "open", "waiting_customer", "resolved", "closed"];
const priorities = ["low", "normal", "high", "urgent"];
const labels = {
  new: "Yeni",
  open: "Açık",
  waiting_customer: "Müşteri bekleniyor",
  resolved: "Çözüldü",
  closed: "Kapalı",
  low: "Düşük",
  normal: "Normal",
  high: "Yüksek",
  urgent: "Acil"
};

function SupportCenterView({
  user,
  userData = {},
  users = [],
  themeMode = "light",
  initialTicketId = "",
  onInitialTicketHandled
}) {
  const management = isManagementHostname();
  const tenantId = resolveTenantId(userData);
  const companyId =
    userData.companyId ||
    userData.companyIds?.[0] ||
    userData.assignedCompanies?.[0] ||
    tenantId;
  const [tickets, setTickets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState([]);
  const [visibility, setVisibility] = useState("public");
  const [showNew, setShowNew] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [messageFontSize, setMessageFontSize] = useState(14);
  const [form, setForm] = useState({
    subject: "",
    category: "Genel",
    priority: "normal",
    message: ""
  });
  const messagesEndRef = useRef(null);
  const dark = themeMode === "dark";
  const colors = {
    surface: dark ? "#162133" : "#fff",
    page: dark ? "#0f172a" : "#f8fafc",
    border: dark ? "#334155" : "#dbe2ea",
    text: dark ? "#e5edf6" : "#124254",
    muted: dark ? "#9fb0c4" : "#64748b",
    subtle: dark ? "#20334a" : "#f1f5f9"
  };

  useEffect(() => {
    if (!management && !tenantId) {
      setError("Hesabınıza tenant ataması yapılmamış.");
      return undefined;
    }
    return subscribeTickets({
      tenantId,
      userId: user.uid,
      management,
      onData: (items) => {
        setTickets(items);
        setSelectedId((current) =>
          items.some((ticket) => ticket.id === current)
            ? current
            : items[0]?.id || ""
        );
      },
      onError: (reason) => setError(reason.message)
    });
  }, [management, tenantId, user.uid]);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return undefined;
    }
    return subscribeMessages({
      ticketId: selectedId,
      management,
      onData: setMessages,
      onError: (reason) => setError(reason.message)
    });
  }, [management, selectedId]);

  useEffect(() => {
    if (!initialTicketId || !tickets.some((ticket) => ticket.id === initialTicketId)) return;
    setSelectedId(initialTicketId);
    setChatOpen(true);
    onInitialTicketHandled?.();
  }, [initialTicketId, onInitialTicketHandled, tickets]);

  useEffect(() => {
    if (chatOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [chatOpen, messages]);

  const selected = useMemo(
    () => tickets.find((ticket) => ticket.id === selectedId),
    [selectedId, tickets]
  );

  const openTicket = (ticketId) => {
    setSelectedId(ticketId);
    setChatOpen(true);
    setError("");
  };

  const submitTicket = async () => {
    if (!form.subject.trim() || !form.message.trim()) return;
    setSaving(true);
    setError("");
    try {
      const id = await createTicket({
        tenantId,
        companyId,
        companyName: userData.companyName || userData.name || "",
        user,
        ...form
      });
      setForm({
        subject: "",
        category: "Genel",
        priority: "normal",
        message: ""
      });
      setShowNew(false);
      setSelectedId(id);
      setChatOpen(true);
    } catch (reason) {
      setError(reason.message);
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    if (!selected || (!draft.trim() && !files.length) || saving) return;
    setSaving(true);
    setError("");
    try {
      await addMessage({
        ticket: selected,
        user,
        body: draft || "Dosya eklendi.",
        visibility: management ? visibility : "public",
        senderType: management ? "staff" : "customer",
        files
      });
      await updateTicket(
        selected.id,
        management
          ? {
            status: visibility === "public" ? "waiting_customer" : selected.status,
            lastMessageAt: new Date()
          }
          : { status: "open", lastMessageAt: new Date() }
      );
      setDraft("");
      setFiles([]);
    } catch (reason) {
      setError(reason.message);
    } finally {
      setSaving(false);
    }
  };

  const renderTicketButton = (ticket, compact = false) => (
    <button
      key={ticket.id}
      onClick={() => openTicket(ticket.id)}
      style={{
        width: "100%",
        textAlign: "left",
        padding: compact ? 11 : 14,
        border: 0,
        borderBottom: `1px solid ${colors.border}`,
        background:
          selectedId === ticket.id
            ? dark ? "#20334a" : "#ecfeff"
            : "transparent",
        color: colors.text,
        cursor: "pointer"
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <strong style={{ fontSize: compact ? 12 : 14 }}>{ticket.subject}</strong>
        <small>{labels[ticket.priority]}</small>
      </div>
      <span
        style={{
          display: "block",
          marginTop: 6,
          color: colors.muted,
          fontSize: 11
        }}
      >
        {management ? `${ticket.companyName || ticket.tenantId} · ` : ""}
        {ticket.aiConversationId ? "Canlı destek" : labels[ticket.status]} · {ticket.category}
      </span>
    </button>
  );

  return (
    <div style={{ color: colors.text }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 18
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Destek Merkezi</h2>
          <p style={{ color: colors.muted, fontWeight: 750 }}>
            {management
              ? "Tüm müşterilerin talepleri ve canlı destek görüşmeleri"
              : "Destek talepleriniz ve Argevio ekibiyle canlı görüşmeleriniz"}
          </p>
        </div>
        {management ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "#0f766e",
              fontWeight: 900
            }}
          >
            <ShieldCheck size={18}/> Operasyon görünümü
          </span>
        ) : (
          <button onClick={() => setShowNew(true)} style={primaryButton}>
            <Plus size={17}/> Yeni talep
          </button>
        )}
      </div>

      {error && (
        <div
          style={{
            background: "#fee2e2",
            color: "#991b1b",
            padding: 11,
            borderRadius: 11,
            marginBottom: 12
          }}
        >
          {error}
        </div>
      )}

      <section
        style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 17,
          overflow: "hidden",
          minHeight: 390
        }}
      >
        <div
          style={{
            padding: "13px 15px",
            borderBottom: `1px solid ${colors.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <strong style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
            <MessageCircle size={18}/> Görüşmeler
          </strong>
          <small style={{ color: colors.muted }}>{tickets.length} kayıt</small>
        </div>
        {tickets.map((ticket) => renderTicketButton(ticket))}
        {!tickets.length && (
          <div style={{ padding: 50, textAlign: "center", color: colors.muted }}>
            <Headphones size={34}/>
            <p>Henüz destek görüşmesi yok.</p>
          </div>
        )}
      </section>

      {chatOpen && selected && (
        <div
          onClick={() => setChatOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2200,
            background: "rgba(15,23,42,.24)",
            backdropFilter: "blur(1px)"
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              position: "absolute",
              top: 24,
              right: 24,
              bottom: 24,
              width: "min(980px, calc(100vw - 220px))",
              maxWidth: "calc(100vw - 32px)",
              borderRadius: 24,
              background: colors.page,
              border: `1px solid ${colors.border}`,
              boxShadow: "0 30px 90px rgba(15,23,42,.28)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <header
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px",
                borderBottom: `1px solid ${colors.border}`,
                background: colors.surface
              }}
            >
              <strong style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <MessageCircle size={20}/> Canlı Destek
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "4px 8px",
                    borderRadius: 999,
                    background: dark ? "#123f38" : "#dcfce7",
                    color: dark ? "#a7f3d0" : "#166534",
                    fontSize: 11
                  }}
                >
                  <Circle size={7} fill="currentColor"/> Gerçek zamanlı
                </span>
              </strong>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={fontControls(colors)}>
                  <button
                    onClick={() => setMessageFontSize((current) => Math.max(10, current - 1))}
                    style={fontButton(colors)}
                  >
                    A-
                  </button>
                  <span style={{ minWidth: 42, textAlign: "center", fontSize: 12 }}>
                    {messageFontSize}px
                  </span>
                  <button
                    onClick={() => setMessageFontSize((current) => Math.min(20, current + 1))}
                    style={fontButton(colors)}
                  >
                    A+
                  </button>
                </div>
                <button
                  onClick={() => setChatOpen(false)}
                  aria-label="Canlı destek penceresini kapat"
                  style={closeButton(colors)}
                >
                  <X size={18}/>
                </button>
              </div>
            </header>

            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: "grid",
                gridTemplateColumns: "230px minmax(0,1fr)",
                gap: 12,
                padding: 16
              }}
            >
              <aside
                style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: 16,
                  background: colors.surface,
                  overflowY: "auto"
                }}
              >
                <div
                  style={{
                    padding: 12,
                    borderBottom: `1px solid ${colors.border}`,
                    display: "flex",
                    justifyContent: "space-between"
                  }}
                >
                  <strong style={{ fontSize: 13 }}>Görüşmeler</strong>
                  <small style={{ color: colors.muted }}>{tickets.length}</small>
                </div>
                {tickets.map((ticket) => renderTicketButton(ticket, true))}
              </aside>

              <section
                style={{
                  minWidth: 0,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 16,
                  background: colors.surface,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                <div
                  style={{
                    padding: "13px 15px",
                    borderBottom: `1px solid ${colors.border}`,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 12
                  }}
                >
                  <div>
                    <strong>{selected.subject}</strong>
                    <small
                      style={{
                        display: "block",
                        marginTop: 5,
                        color: colors.muted
                      }}
                    >
                      {selected.companyName || selected.tenantId} · #{selected.id.slice(0, 8)}
                    </small>
                  </div>
                  {management && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "110px 100px 130px",
                        gap: 6
                      }}
                    >
                      <select
                        value={selected.status}
                        onChange={(event) =>
                          updateTicket(selected.id, { status: event.target.value })
                        }
                      >
                        {statuses.map((status) => (
                          <option key={status} value={status}>{labels[status]}</option>
                        ))}
                      </select>
                      <select
                        value={selected.priority}
                        onChange={(event) =>
                          updateTicket(selected.id, { priority: event.target.value })
                        }
                      >
                        {priorities.map((priority) => (
                          <option key={priority} value={priority}>{labels[priority]}</option>
                        ))}
                      </select>
                      <select
                        value={selected.assigneeId || ""}
                        onChange={(event) =>
                          updateTicket(selected.id, {
                            assigneeId: event.target.value,
                            assigneeName: event.target.selectedOptions[0]?.text || ""
                          })
                        }
                      >
                        <option value="">Atanmamış</option>
                        {users.filter((item) => item.isActive !== false).map((item) => (
                          <option key={item.id || item.uid} value={item.id || item.uid}>
                            {item.name || item.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: 16,
                    background: dark ? "#111b2b" : "#f8fafc",
                    fontSize: messageFontSize
                  }}
                >
                  {messages.map((message) => {
                    const mine = management
                      ? message.senderType === "staff"
                      : message.senderType === "customer";
                    const internal = message.visibility === "internal";
                    return (
                      <div
                        key={message.id}
                        style={{
                          display: "flex",
                          justifyContent: internal
                            ? "center"
                            : mine ? "flex-end" : "flex-start",
                          marginBottom: 12
                        }}
                      >
                        <article
                          style={{
                            width: "fit-content",
                            maxWidth: internal ? "88%" : "76%",
                            padding: "10px 13px",
                            borderRadius: internal
                              ? 12
                              : mine
                                ? "16px 16px 4px 16px"
                                : "16px 16px 16px 4px",
                            background: internal
                              ? dark ? "#3b3420" : "#fffbeb"
                              : mine ? "#0f766e" : dark ? "#20334a" : "#fff",
                            color: internal
                              ? dark ? "#fde68a" : "#854d0e"
                              : mine ? "#fff" : colors.text,
                            border: internal
                              ? "1px solid #f59e0b"
                              : mine ? "none" : `1px solid ${colors.border}`,
                            boxShadow: "0 4px 12px rgba(15,23,42,.05)"
                          }}
                        >
                          <small
                            style={{
                              display: "block",
                              marginBottom: 5,
                              opacity: .72
                            }}
                          >
                            {message.senderName} · {internal
                              ? "İç not"
                              : formatMessageTime(message.createdAt)}
                          </small>
                          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                            {message.body}
                          </div>
                          {message.attachments?.map((attachment) => (
                            <a
                              key={attachment.path}
                              href={attachment.url}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                display: "block",
                                marginTop: 7,
                                color: mine ? "#ccfbf1" : "#0f766e",
                                fontWeight: 800
                              }}
                            >
                              {attachment.name}
                            </a>
                          ))}
                        </article>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef}/>
                </div>

                <div
                  style={{
                    borderTop: `1px solid ${colors.border}`,
                    padding: 12,
                    background: colors.surface
                  }}
                >
                  {management && (
                    <div style={{ display: "flex", gap: 7, marginBottom: 8 }}>
                      <button
                        onClick={() => setVisibility("public")}
                        style={visibilityButton(visibility === "public")}
                      >
                        <Send size={14}/> Müşteriye yanıt
                      </button>
                      <button
                        onClick={() => setVisibility("internal")}
                        style={visibilityButton(visibility === "internal", true)}
                      >
                        İç not
                      </button>
                    </div>
                  )}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto auto",
                      gap: 8,
                      alignItems: "end"
                    }}
                  >
                    <textarea
                      rows="2"
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                          event.preventDefault();
                          send();
                        }
                      }}
                      placeholder={
                        visibility === "internal" && management
                          ? "Yalnızca iç ekibin göreceği not…"
                          : "Mesajınızı yazın…"
                      }
                      style={{
                        padding: 11,
                        borderRadius: 12,
                        border: `1px solid ${colors.border}`,
                        resize: "none",
                        background: dark ? "#111827" : "#fff",
                        color: colors.text,
                        font: "inherit"
                      }}
                    />
                    <label
                      title="Dosya ekle"
                      style={{
                        width: 42,
                        height: 42,
                        display: "grid",
                        placeItems: "center",
                        border: `1px solid ${colors.border}`,
                        borderRadius: 11,
                        cursor: "pointer",
                        background: colors.subtle
                      }}
                    >
                      <Paperclip size={18}/>
                      <input
                        type="file"
                        multiple
                        onChange={(event) => setFiles([...event.target.files])}
                        style={{ display: "none" }}
                      />
                    </label>
                    <button
                      disabled={saving || (!draft.trim() && !files.length)}
                      onClick={send}
                      style={{
                        ...primaryButton,
                        width: 44,
                        height: 42,
                        justifyContent: "center",
                        padding: 0,
                        opacity: saving || (!draft.trim() && !files.length) ? .55 : 1
                      }}
                    >
                      <Send size={18}/>
                    </button>
                  </div>
                  {files.length > 0 && (
                    <small
                      style={{
                        display: "block",
                        marginTop: 7,
                        color: colors.muted
                      }}
                    >
                      {files.map((file) => file.name).join(", ")}
                    </small>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <div style={{ ...modalOverlay, zIndex: 2400 }}>
          <div
            style={{
              width: "min(540px,94vw)",
              background: colors.surface,
              borderRadius: 18,
              padding: 22
            }}
          >
            <h3>Yeni destek talebi</h3>
            <input
              style={inputStyle}
              placeholder="Konu"
              value={form.subject}
              onChange={(event) => setForm({ ...form, subject: event.target.value })}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8
              }}
            >
              <select
                style={inputStyle}
                value={form.category}
                onChange={(event) => setForm({ ...form, category: event.target.value })}
              >
                <option>Genel</option>
                <option>Teknik</option>
                <option>Mevzuat</option>
                <option>Süreç</option>
                <option>Faturalama</option>
              </select>
              <select
                style={inputStyle}
                value={form.priority}
                onChange={(event) => setForm({ ...form, priority: event.target.value })}
              >
                {priorities.map((priority) => (
                  <option key={priority} value={priority}>{labels[priority]}</option>
                ))}
              </select>
            </div>
            <textarea
              style={{ ...inputStyle, resize: "vertical" }}
              rows="6"
              placeholder="Talebinizi açıklayın…"
              value={form.message}
              onChange={(event) => setForm({ ...form, message: event.target.value })}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setShowNew(false)}>Vazgeç</button>
              <button disabled={saving} onClick={submitTicket} style={primaryButton}>
                Talebi gönder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const primaryButton = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  border: 0,
  borderRadius: 11,
  padding: "10px 14px",
  background: "#0f766e",
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer"
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: 11,
  border: "1px solid #cbd5e1",
  borderRadius: 11,
  marginBottom: 9
};

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,35,45,.65)",
  display: "grid",
  placeItems: "center",
  padding: 20
};

const fontControls = (colors) => ({
  display: "flex",
  alignItems: "center",
  gap: 5,
  padding: 4,
  borderRadius: 12,
  border: `1px solid ${colors.border}`,
  background: colors.page
});

const fontButton = (colors) => ({
  minWidth: 34,
  height: 28,
  border: 0,
  borderRadius: 9,
  background: colors.subtle,
  color: colors.text,
  fontWeight: 950,
  cursor: "pointer"
});

const closeButton = (colors) => ({
  width: 34,
  height: 34,
  borderRadius: 12,
  border: `1px solid ${colors.border}`,
  background: colors.page,
  color: colors.text,
  display: "grid",
  placeItems: "center",
  cursor: "pointer"
});

const visibilityButton = (active, internal = false) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  border: `1px solid ${active ? internal ? "#f59e0b" : "#0f766e" : "#cbd5e1"}`,
  borderRadius: 9,
  padding: "7px 10px",
  background: active ? internal ? "#fffbeb" : "#ecfdf5" : "#fff",
  color: internal ? "#92400e" : "#0f766e",
  fontWeight: 800,
  cursor: "pointer"
});

const formatMessageTime = (value) => {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("tr-TR", {
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
};

export default SupportCenterView;
